import { ceilToMultipleOfTen } from './ceilToMultipleOfTen';
import { toNumber } from './toNumber';

export function calculateSafeMax<T>(
  data: T[],
  valueExtractor: (data: T) => unknown,
  min: number,
  maxFallback: number = 1000
): number {
  const values: number[] = [];
  if (data && data.length > 0) {
    for (const point of data) {
      const raw = valueExtractor(point);
      const maybeNum = toNumber(raw);
      if (maybeNum !== null && maybeNum >= min) {
        values.push(maybeNum);
      }
    }
  }

  if (values.length === 0) {
    return maxFallback;
  }

  const robustMax = computeRobustUpperBound(values, 0.95, true);

  // Ensure max is at least slightly above min to avoid divide-by-zero in gradient
  const roundedMax = ceilToMultipleOfTen(robustMax);
  const safeMax = Number.isFinite(roundedMax)
    ? Math.max(roundedMax, min + 1)
    : maxFallback;

  return safeMax;
}

function computeQuantile(sortedValues: number[], q: number): number {
  if (sortedValues.length === 0) return 0;
  const pos = (sortedValues.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sortedValues[base + 1] !== undefined) {
    return (
      sortedValues[base] + rest * (sortedValues[base + 1] - sortedValues[base])
    );
  } else {
    return sortedValues[base];
  }
}

function computeRobustUpperBound(
  values: number[],
  percentile: number,
  useIqrUpperFence: boolean
): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const maxValue = sorted[sorted.length - 1];
  const p = Math.max(0, Math.min(1, percentile));
  const pVal = computeQuantile(sorted, p);

  if (!useIqrUpperFence) {
    return Math.min(pVal, maxValue);
  }

  const q1 = computeQuantile(sorted, 0.25);
  const q3 = computeQuantile(sorted, 0.75);
  const iqr = q3 - q1;
  const upperFence = q3 + 1.5 * iqr;
  return Math.min(pVal, upperFence, maxValue);
}
